import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { User, Buildings, Gear, Globe, MapPin, FloppyDisk, SpinnerGap, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENTS = [
  { id: "restaurante", label: "Restaurante / Bar", emoji: "🍽️" },
  { id: "confeitaria", label: "Confeitaria / Padaria", emoji: "🎂" },
  { id: "eventos", label: "Eventos / Buffet", emoji: "🎪" },
  { id: "delivery", label: "Delivery / Dark Kitchen", emoji: "🍕" },
  { id: "hotelaria", label: "Hotelaria", emoji: "🏨" },
  { id: "autonomo", label: "Chef Autônomo", emoji: "🧑‍🍳" },
  { id: "food_truck", label: "Food Truck", emoji: "🚚" },
  { id: "escola", label: "Escola / Ensino", emoji: "📚" },
  { id: "industria", label: "Indústria", emoji: "🏭" },
  { id: "outro", label: "Outro", emoji: "➕" },
];

const MODULES = [
  { id: "advanced_stock", pilar: 1, name: "Estoque Avançado", desc: "Lotes, validade, multi-almoxarifado" },
  { id: "advanced_production", pilar: 2, name: "Produção Complexa", desc: "Sub-receitas, ordens de produção" },
  { id: "financial_intelligence", pilar: 3, name: "Inteligência Financeira", desc: "CMV, DRE, histórico de preços" },
  { id: "rbac", pilar: 4, name: "Multi-usuário", desc: "Equipe, permissões, praças" },
  { id: "supply_chain", pilar: 5, name: "Logística", desc: "Fornecedores, aprovação de compras" },
  { id: "menu_intelligence", pilar: 6, name: "Cardápio e Vendas", desc: "Engenharia de cardápio" },
  { id: "business_intelligence", pilar: 7, name: "BI e Relatórios", desc: "Dashboards, alertas" },
  { id: "menu_builder", pilar: 8, name: "Cardápios e Menus", desc: "Link público, WhatsApp" },
  { id: "checklists", pilar: 9, name: "Checklists", desc: "Abertura, fechamento, templates" },
];

const LANGUAGES = [
  { code: "pt-BR", label: "Português (BR)", flag: "🇧🇷" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
];

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessSegment, setBusinessSegment] = useState("");
  const [language, setLanguage] = useState("pt-BR");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [enabledModules, setEnabledModules] = useState([]);
  const [businessType, setBusinessType] = useState("autonomo");
  const [accountType, setAccountType] = useState("chef_autonomo");

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setBusinessName(user.business_name || "");
      setBusinessSegment(user.business_segment || "");
      setLanguage(user.language || "pt-BR");
      setCountry(user.country || "");
      setState(user.state || "");
      setCity(user.city || "");
      setEnabledModules(user.enabled_modules || []);
      setBusinessType(user.business_type || "autonomo");
      setAccountType(user.account_type || (user.business_type === "empresa" ? "empresa" : "chef_autonomo"));
    }
  }, [user]);

  const toggleModule = (id) => {
    setEnabledModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/user/profile`, {
        name,
        business_name: businessName,
        business_segment: businessSegment,
        business_type: businessType,
        account_type: accountType,
        language,
        country,
        state,
        city,
        enabled_modules: enabledModules,
      });
      await checkAuth();
      setSaved(true);
      toast.success("Perfil atualizado!");
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };


  if (!user) return null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto pb-24" data-testid="profile-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
          <User size={24} weight="bold" /> Meu Perfil
        </h2>
        <p className="text-sm text-zinc-500 mt-1">Configure seu perfil, segmento e módulos ativos</p>
      </div>

      {/* Personal Info */}
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <User size={16} className="text-zinc-600" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Dados Pessoais</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Email</Label>
              <Input value={user.email} disabled className="mt-1 bg-zinc-50 text-zinc-400" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Nome do Negócio</Label>
              <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Ex: Doces da Ana" className="mt-1" data-testid="profile-business-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Idioma</Label>
              <div className="flex flex-col sm:flex-row gap-1.5 mt-1">
                {LANGUAGES.map(l => (
                  <button key={l.code} onClick={() => setLanguage(l.code)} data-testid={`profile-lang-${l.code}`}
                    className={`flex-1 p-2 rounded-lg border text-xs text-center transition-all ${language === l.code ? 'border-zinc-950 bg-zinc-50 font-bold' : 'border-zinc-200 hover:border-zinc-300'}`}>
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={16} className="text-zinc-600" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Localização</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">País</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1" data-testid="profile-country" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Estado</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} className="mt-1" data-testid="profile-state" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Cidade</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1" data-testid="profile-city" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Segment */}
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Buildings size={16} className="text-zinc-600" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Tipo de Conta</h3>
          </div>
          {user?.is_employee ? (
            <p className="text-sm text-zinc-500">Sua conta está vinculada à equipe de {user?.business_name || "uma empresa"}.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button onClick={() => { setAccountType("chef_autonomo"); setBusinessType("autonomo"); }} data-testid="profile-type-autonomo"
                  className={`p-3 rounded-lg border text-center text-sm transition-all ${accountType === "chef_autonomo" ? 'border-zinc-950 bg-zinc-50 font-bold' : 'border-zinc-200 hover:border-zinc-300'}`}>
                  🧑‍🍳 Chef Autônomo
                </button>
                <button onClick={() => { setAccountType("microempreendimento"); setBusinessType("empresa"); }} data-testid="profile-type-micro"
                  className={`p-3 rounded-lg border text-center text-sm transition-all ${accountType === "microempreendimento" ? 'border-zinc-950 bg-zinc-50 font-bold' : 'border-zinc-200 hover:border-zinc-300'}`}>
                  🏪 Microempreendimento
                </button>
                <button onClick={() => { setAccountType("empresa"); setBusinessType("empresa"); }} data-testid="profile-type-empresa"
                  className={`p-3 rounded-lg border text-center text-sm transition-all ${accountType === "empresa" ? 'border-zinc-950 bg-zinc-50 font-bold' : 'border-zinc-200 hover:border-zinc-300'}`}>
                  🏢 Empresa
                </button>
              </div>
              <p className="text-xs text-zinc-400">Você pode alternar quando quiser. Qualquer conta pode montar uma equipe pela aba Equipe (módulo Multi-usuário).</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Business Segment */}
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Buildings size={16} className="text-zinc-600" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Segmento de Atuação</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            {SEGMENTS.map(seg => (
              <button key={seg.id} onClick={() => setBusinessSegment(seg.id)} data-testid={`profile-segment-${seg.id}`}
                className={`p-2 rounded-lg border text-center text-xs transition-all ${
                  businessSegment === seg.id ? 'border-zinc-950 bg-zinc-50 font-bold' : 'border-zinc-200 hover:border-zinc-300'
                }`}>
                <span className="text-lg block mb-0.5">{seg.emoji}</span>
                {seg.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modules */}
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Gear size={16} className="text-zinc-600" weight="bold" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">Módulos Ativos</h3>
          </div>
          <p className="text-xs text-zinc-400">Ative ou desative módulos conforme a necessidade da sua operação</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {MODULES.map(mod => (
              <div key={mod.id} onClick={() => toggleModule(mod.id)} role="button" tabIndex={0} data-testid={`profile-module-${mod.id}`}
                className={`flex items-center gap-2.5 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                  enabledModules.includes(mod.id) ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                }`}>
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${enabledModules.includes(mod.id) ? 'bg-zinc-950 border-zinc-950' : 'border-zinc-300'}`}>
                  {enabledModules.includes(mod.id) && <Check size={10} className="text-white" weight="bold" />}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-zinc-900"><span className="text-zinc-400 mr-1">P{mod.pilar}</span>{mod.name}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{mod.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-zinc-950 text-white hover:bg-zinc-800 text-sm font-bold" data-testid="save-profile-btn">
        {saving ? <SpinnerGap size={18} className="animate-spin mr-2" /> : saved ? <Check size={18} className="mr-2" weight="bold" /> : <FloppyDisk size={18} className="mr-2" />}
        {saving ? "Salvando..." : saved ? "Salvo!" : "Salvar Alterações"}
      </Button>
    </div>
  );
}
